import { ArrowLeft, ChevronDown, ChevronUp } from "lucide-react";
import { motion } from "motion/react";
import { useEffect, useState } from "react";

import { trackPageView } from "../utils/analytics";

import { KoruLogo } from "./brand/KoruLogo";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";

interface LegalPageProps {
  onBack: () => void;
}

interface AccordionSectionProps {
  title: string;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function AccordionSection({
  title,
  isExpanded,
  onToggle,
  children,
}: AccordionSectionProps) {
  return (
    <Card className="bg-card border-border">
      <button
        onClick={onToggle}
        aria-expanded={isExpanded}
        className="w-full p-4 sm:p-6 flex items-center justify-between text-left hover:bg-muted/50 transition-colors rounded-t-lg"
      >
        <h2 className="text-lg sm:text-xl font-semibold text-foreground">
          {title}
        </h2>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-muted-foreground flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
        )}
      </button>
      {isExpanded && (
        <CardContent className="p-4 sm:p-6 pt-0 border-t border-border">
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="prose prose-invert max-w-none"
          >
            {children}
          </motion.div>
        </CardContent>
      )}
    </Card>
  );
}

export default function LegalPage({ onBack }: LegalPageProps) {
  const [expandedSection, setExpandedSection] = useState<string | null>("tos");

  useEffect(() => {
    trackPageView("/legal", "Koru - Legal");
  }, []);

  const toggleSection = (section: string) => {
    setExpandedSection(prev => (prev === section ? null : section));
  };

  return (
    <div
      data-testid="legal-page-container"
      className="min-h-screen bg-background text-foreground"
    >
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={onBack}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </Button>
          <div className="flex flex-col items-center">
            <KoruLogo className="w-8 h-6 svg-glow-primary" />
            <span className="text-sm font-semibold text-foreground">Koru</span>
          </div>
          <div className="w-16" /> {/* Spacer for centering */}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Page Title */}
          <div className="text-center mb-8 sm:mb-12">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-2">
              Legal
            </h1>
            <p className="text-muted-foreground">
              Our terms and privacy policies
            </p>
          </div>

          {/* Accordion Sections */}
          <div className="space-y-4">
            {/* Terms of Service */}
            <AccordionSection
              title="Terms of Service"
              isExpanded={expandedSection === "tos"}
              onToggle={() => toggleSection("tos")}
            >
              <div className="space-y-6 text-muted-foreground">
                <p className="text-sm text-muted-foreground/70">
                  Last Updated: February 2026
                </p>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    1. Service Description
                  </h3>
                  <p>
                    Koru is an AI-powered communication platform designed to
                    help individuals, couples, co-parents, and groups
                    communicate more effectively. Our service provides AI-guided
                    conversations, communication analytics, and relationship
                    insights to help you build stronger connections.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    2. Acceptance of Terms
                  </h3>
                  <p>
                    By accessing or using Koru, you agree to be bound by these
                    Terms of Service. If you do not agree to these terms, please
                    do not use our service.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    3. Payment Terms
                  </h3>
                  <p>
                    Koru offers both free and paid subscription tiers. Paid
                    subscriptions are billed in advance on a monthly or annual
                    basis. You authorize us to charge your payment method for
                    the subscription fee at the beginning of each billing
                    period. Prices are subject to change with 30 days&apos;
                    notice.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    4. Refund Policy
                  </h3>
                  <p>
                    We offer a 30-day money-back guarantee for new subscribers.
                    If you are not satisfied with our service within the first
                    30 days of your subscription, contact us at
                    support@koruapp.com for a full refund. After the 30-day
                    period, refunds are provided at our discretion on a
                    case-by-case basis.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    5. User Conduct
                  </h3>
                  <p>
                    You agree not to use Koru for any unlawful purpose or in any
                    way that could harm, disable, or impair our service. You are
                    responsible for all content you share through the platform.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    6. Limitation of Liability
                  </h3>
                  <p>
                    Koru is provided &quot;as is&quot; without warranties of any
                    kind. To the maximum extent permitted by law, Koru and its
                    affiliates shall not be liable for any indirect, incidental,
                    special, consequential, or punitive damages, or any loss of
                    profits or revenues, whether incurred directly or
                    indirectly, or any loss of data, use, goodwill, or other
                    intangible losses.
                  </p>
                  <p className="mt-2">
                    Koru is not a substitute for professional therapy, medical
                    advice, or legal counsel. If you are in crisis, please
                    contact appropriate emergency services or mental health
                    professionals.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    7. Termination
                  </h3>
                  <p>
                    We reserve the right to suspend or terminate your account at
                    any time for violations of these terms. You may cancel your
                    account at any time through your account settings.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    8. Changes to Terms
                  </h3>
                  <p>
                    We may update these terms from time to time. We will notify
                    you of significant changes via email or through the app.
                    Continued use of Koru after changes constitutes acceptance
                    of the updated terms.
                  </p>
                </section>
              </div>
            </AccordionSection>

            {/* Privacy Policy */}
            <AccordionSection
              title="Privacy Policy"
              isExpanded={expandedSection === "privacy"}
              onToggle={() => toggleSection("privacy")}
            >
              <div className="space-y-6 text-muted-foreground">
                <p className="text-sm text-muted-foreground/70">
                  Last Updated: February 2026
                </p>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    1. Information We Collect
                  </h3>
                  <p>We collect the following types of information:</p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>
                      <strong>Account Information:</strong> Name, email address,
                      and payment information when you create an account.
                    </li>
                    <li>
                      <strong>Conversation Data:</strong> Messages and
                      interactions within the Koru platform to provide our AI
                      guidance services.
                    </li>
                    <li>
                      <strong>Usage Data:</strong> How you interact with our
                      app, including features used and session duration.
                    </li>
                    <li>
                      <strong>Device Information:</strong> Device type,
                      operating system, and browser type for optimization
                      purposes.
                    </li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    2. How We Use Your Information
                  </h3>
                  <p>We use your information to:</p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>
                      Provide and improve our AI-powered communication services
                    </li>
                    <li>Process payments and manage subscriptions</li>
                    <li>Send service-related communications</li>
                    <li>Analyze usage patterns to improve our platform</li>
                    <li>Ensure security and prevent fraud</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    3. Data Security
                  </h3>
                  <p>
                    We implement industry-standard security measures including
                    encryption in transit and at rest, secure authentication,
                    and regular security audits to protect your personal
                    information.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    4. California Privacy Rights (CCPA)
                  </h3>
                  <p>
                    If you are a California resident, you have specific rights
                    under the California Consumer Privacy Act (CCPA):
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>
                      <strong>Right to Know:</strong> You can request
                      information about the personal data we collect about you.
                    </li>
                    <li>
                      <strong>Right to Delete:</strong> You can request deletion
                      of your personal data, subject to certain exceptions.
                    </li>
                    <li>
                      <strong>Right to Opt-Out:</strong> You can opt out of the
                      sale of your personal information.
                    </li>
                    <li>
                      <strong>Right to Non-Discrimination:</strong> We will not
                      discriminate against you for exercising your privacy
                      rights.
                    </li>
                  </ul>
                  <p className="mt-2">
                    To exercise these rights, contact us at privacy@koruapp.com.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    5. Do Not Sell My Personal Information
                  </h3>
                  <p>
                    Koru does not sell your personal information to third
                    parties. We may share data with service providers who help
                    us operate our platform, but only under strict
                    confidentiality agreements and for the sole purpose of
                    providing our services.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    6. Data Retention
                  </h3>
                  <p>
                    We retain your personal information for as long as your
                    account is active or as needed to provide services.
                    Conversation data may be retained for up to 90 days after
                    account deletion for backup purposes, after which it is
                    permanently deleted.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    7. Third-Party Services
                  </h3>
                  <p>
                    We use third-party services for payment processing (Stripe),
                    analytics, and infrastructure. These services have their own
                    privacy policies and we encourage you to review them.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    8. Contact Us
                  </h3>
                  <p>
                    For privacy-related inquiries or to exercise your data
                    rights, please contact us at:
                  </p>
                  <p className="mt-2">
                    <strong>Email:</strong> privacy@koruapp.com
                  </p>
                </section>
              </div>
            </AccordionSection>
          </div>

          {/* Bottom padding for mobile */}
          <div className="h-8" />
        </motion.div>
      </main>
    </div>
  );
}
